import React, { useEffect, useMemo, useRef, useState } from 'react';
import { HorseFilters, Country } from '../types/api';
import { Search, Filter, Loader2 } from 'lucide-react';
import { apiService } from '../services/api';
import { RangeSlider } from './RangeSlider';
import { formatHorseName } from '../utils/horseName';

interface FilterControlsProps {
  onApplyFilters: (filters: HorseFilters) => void;
  currentFilters: HorseFilters;
  forceExpanded?: boolean;
  onFiltersChange?: (filters: HorseFilters) => void;
  hideActions?: boolean;
  hideHeader?: boolean;
  /** Real country list from the backend */
  countries?: Country[];
  /** When provided, name autocomplete is drawn from this list instead of the backend */
  collectionHorseNames?: string[];
}

const CURRENT_YEAR = new Date().getFullYear();

const RECENCY_OPTIONS = [
  { label: '3m', within: 3, unit: 'months' as const },
  { label: '6m', within: 6, unit: 'months' as const },
  { label: '1y', within: 1, unit: 'years' as const },
  { label: '2y', within: 2, unit: 'years' as const },
];

export const FilterControls: React.FC<FilterControlsProps> = ({
  onApplyFilters,
  currentFilters,
  forceExpanded = false,
  onFiltersChange,
  hideActions = false,
  hideHeader = false,
  countries = [],
  collectionHorseNames,
}) => {
  const [isExpanded, setIsExpanded] = useState(forceExpanded);
  const [localFilters, setLocalFilters] = useState<HorseFilters>(currentFilters);
  const [nameSuggestions, setNameSuggestions] = useState<string[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const debounceRef = useRef<number | undefined>(undefined);
  const [countrySearch, setCountrySearch] = useState('');
  const isLocalUpdate = useRef(false);

  const sexOptions = useMemo(() => ['Stallion', 'Mare', 'Gelding'], []);

  const filteredCountries = useMemo(() => {
    if (!countrySearch) return countries;
    const lower = countrySearch.toLowerCase();
    return countries.filter(c => c.name.toLowerCase().includes(lower));
  }, [countries, countrySearch]);

  useEffect(() => {
    if (isLocalUpdate.current) {
      isLocalUpdate.current = false;
      return;
    }
    setLocalFilters(currentFilters);
  }, [currentFilters]);

  useEffect(() => {
    setIsExpanded(forceExpanded);
  }, [forceExpanded]);

  const handleInputChange = (key: keyof HorseFilters, value: any) => {
    setLocalFilters(prev => {
      const next = { ...prev };
      if (value === '' || value === undefined || value === null) {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  };

  const handleToggleMulti = (key: 'sex' | 'countries', value: string) => {
    setLocalFilters(prev => {
      const current = (prev[key] as string[] | undefined) || [];
      const nextValues = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      const next: HorseFilters = { ...prev };
      if (nextValues.length === 0) {
        delete next[key];
      } else {
        (next as any)[key] = nextValues;
      }
      return next;
    });
  };

  useEffect(() => {
    isLocalUpdate.current = true;
    onFiltersChange?.(localFilters);
  }, [localFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNameSuggestion = (name: string) => {
    handleInputChange('name', name);
    setNameSuggestions([]);
  };

  // Debounced name autocomplete
  useEffect(() => {
    const term = localFilters.name?.trim() || '';
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (term.length < 2) {
      setNameSuggestions([]);
      return;
    }

    if (collectionHorseNames) {
      // Filter in-memory collection names — no API call needed
      const lower = term.toLowerCase();
      const matches = collectionHorseNames
        .filter(n => n.toLowerCase().includes(lower))
        .slice(0, 6);
      setNameSuggestions(matches);
      return;
    }

    debounceRef.current = window.setTimeout(async () => {
      setLoadingSuggestions(true);
      try {
        const results = await apiService.searchHorses({ name: term }, 0, 6);
        const names = Array.from(
          new Set(results.horses.map(h => h.unified_horse_name).filter(Boolean))
        );
        setNameSuggestions(names.slice(0, 6));
      } catch (err) {
        console.warn('Name autocomplete failed', err);
        setNameSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 250);
  }, [localFilters.name, collectionHorseNames]);

  const handleApply = () => {
    onApplyFilters(localFilters);
    setIsExpanded(false);
  };

  const handleReset = () => {
    setLocalFilters({});
    onApplyFilters({});
    onFiltersChange?.({});
    setIsExpanded(false);
  };

  return (
    <div className="mb-6">
      <div className="p-0">
        {!hideHeader && (
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Filter size={20} className="text-gray-500 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
            </div>
            {!forceExpanded && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {isExpanded ? 'Hide' : 'Show'} Advanced Filters
              </button>
            )}
          </div>
        )}

        {/* Basic Search with autocomplete */}
        <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Horse Name
            </label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                id="name"
                value={localFilters.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Search by horse name..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                autoComplete="off"
              />
              {loadingSuggestions && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
              )}
            </div>
            {nameSuggestions.length > 0 && (
              <div className="mt-1 border border-gray-200 rounded-md bg-white shadow-sm divide-y max-h-56 overflow-auto">
                {nameSuggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => handleNameSuggestion(suggestion)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                  >
                    {formatHorseName(suggestion)}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label htmlFor="fei_id" className="block text-sm font-medium text-gray-700 mb-1">
              FEI ID
            </label>
            <input
              type="text"
              id="fei_id"
              value={localFilters.fei_id || ''}
              onChange={(e) => handleInputChange('fei_id', e.target.value)}
              placeholder="e.g. 109IW94"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        </div>

        {/* Advanced Filters */}
        {isExpanded && (
          <div className="space-y-4 border-t border-gray-200 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

              {/* Birth Year Slider */}
              <RangeSlider
                label="Birth Year"
                valueMin={localFilters.birth_year_min}
                valueMax={localFilters.birth_year_max}
                minLimit={2000}
                maxLimit={CURRENT_YEAR}
                step={1}
                onChange={(min, max) => {
                  handleInputChange('birth_year_min', min);
                  handleInputChange('birth_year_max', max);
                }}
              />

              {/* Show Level Slider */}
              <RangeSlider
                label="Show Level (cm)"
                valueMin={localFilters.show_level_min}
                valueMax={localFilters.show_level_max}
                minLimit={100}
                maxLimit={165}
                step={5}
                unit="cm"
                onChange={(min, max) => {
                  handleInputChange('show_level_min', min);
                  handleInputChange('show_level_max', max);
                }}
              />

              {/* Country */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                <div className="relative">
                  <details className="group">
                    <summary className="flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 select-none">
                      <span>
                        {(localFilters.countries?.length ?? 0) > 0
                          ? `${localFilters.countries?.length} selected`
                          : 'Select countries'}
                      </span>
                      <span className="text-gray-400 group-open:rotate-180 transition-transform">⌄</span>
                    </summary>
                    <div className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
                      <div className="p-2 border-b border-gray-100">
                        <input
                          type="text"
                          value={countrySearch}
                          onChange={e => setCountrySearch(e.target.value)}
                          placeholder="Search countries…"
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                          onClick={e => e.stopPropagation()}
                        />
                      </div>
                      <div className="max-h-56 overflow-auto">
                        {filteredCountries.map((c) => {
                          const checked = (localFilters.countries || []).includes(c.code);
                          return (
                            <button
                              key={c.code}
                              type="button"
                              onClick={() => handleToggleMulti('countries', c.code)}
                              className="w-full flex items-center px-3 py-2 text-sm hover:bg-gray-50"
                            >
                              <span
                                className={`mr-2 h-4 w-4 flex-shrink-0 inline-flex items-center justify-center border rounded ${
                                  checked ? 'bg-blue-500 border-blue-500 text-white' : 'border-gray-300 text-transparent'
                                }`}
                              >
                                ✓
                              </span>
                              {c.name}
                            </button>
                          );
                        })}
                        {filteredCountries.length === 0 && (
                          <p className="px-3 py-2 text-sm text-gray-400">No countries found</p>
                        )}
                      </div>
                    </div>
                  </details>
                </div>
              </div>

              {/* Sex */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sex</label>
                <div className="space-y-2">
                  {sexOptions.map((option) => (
                    <label key={option} className="flex items-center text-sm text-gray-700">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        checked={(localFilters.sex || []).includes(option)}
                        onChange={() => handleToggleMulti('sex', option)}
                      />
                      <span className="ml-2">{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Pony filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Horse Type</label>
                <select
                  value={localFilters.is_pony === true ? 'true' : localFilters.is_pony === false ? 'false' : ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    handleInputChange('is_pony', val === '' ? undefined : val === 'true');
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="">All</option>
                  <option value="true">Ponies only</option>
                  <option value="false">Non-ponies only</option>
                </select>
              </div>

              {/* Has Videos */}
              <div className="flex items-center pt-6">
                <label className="flex items-center text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    checked={localFilters.has_videos === true}
                    onChange={(e) => handleInputChange('has_videos', e.target.checked ? true : undefined)}
                  />
                  <span className="ml-2">Has videos</span>
                </label>
              </div>

              {/* Last Result Within */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Active within</label>
                <div className="flex gap-2">
                  {RECENCY_OPTIONS.map((opt) => {
                    const isSelected =
                      localFilters.last_result_within === opt.within &&
                      localFilters.last_result_unit === opt.unit;
                    return (
                      <button
                        key={opt.label}
                        type="button"
                        onClick={() => {
                          if (isSelected) {
                            handleInputChange('last_result_within', undefined);
                            handleInputChange('last_result_unit', undefined);
                          } else {
                            handleInputChange('last_result_within', opt.within);
                            handleInputChange('last_result_unit', opt.unit);
                          }
                        }}
                        className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                          isSelected
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

            </div>
          </div>
        )}

        {!hideActions && (
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              onClick={handleReset}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Reset
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Apply Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
